import qs from 'qs';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppointmentDispatchTypes, useAppointmentDispatch } from '../../context/Appointment/appointment';
import { Types, useCartDispatch } from '../../context/cart';
import { useKioskState, useKioskDispatch, usePossibleDealers, useAdditionalDealers, useSwitchDealer, useTowInSupport } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { LocalizationButtons } from '../../controls/LocalizationButtons/LocalizationButtons';
import { ThemeImage } from '../../controls/ThemeImage/ThemeImage';
import { formatAsPhoneNumber } from '../../helpers/Number';
import { default as css } from './OutdoorLanding.module.scss';
import { GMAccessibilityButton } from '../../controls/ButtonBase/index';
import { AppRoutePaths, AppointmentRoutePaths, CustomerRoutePaths, Customer_AddRoutesPaths, PathName } from '../../helpers/Routes';
import { KioskDispatchTypes } from '../../context/Kiosk/kiosk.types';
import { GMButton } from '../../controls/Button';
import { GMRoundedIcon, GMSizedIcon } from '../../controls/Icon';
import { useNavigateAndRecord } from '../../helpers/Hooks';
import type { ConfirmInformationQueryParams } from '../Appointment/ConfirmInformation/ConfirmInformation.types';
import type { GoMotoComponent } from '../../API/GoMoto.types';

export const OutdoorLanding: GoMotoComponent = () => {
    const kiosk = useKioskState();
    const navigate = useNavigate();
    const Localization = useLocalization();
    const { search } = useLocation();
    const appointmentDispatch = useAppointmentDispatch();
    const kioskDispatch = useKioskDispatch();
    const switchDealer = useSwitchDealer();
    const additionalDealers = useAdditionalDealers();
    const possibleDealers = usePossibleDealers();
    const cartDispatch = useCartDispatch();
    const towInSupport = useTowInSupport();
    const { navigateAndRecord } = useNavigateAndRecord();

    useEffect(() => {
        /**
         * Remove any existing appointment; keep PII masks
         */
        appointmentDispatch({ type: AppointmentDispatchTypes.RemoveAndKeepMasks });
        cartDispatch({ type: Types.Clear });

        /**
         * Clear any possible dealer filtering from prior check-ins
         */
        possibleDealers && kioskDispatch({ type: KioskDispatchTypes.ClearPossibleDealers });

        /**
         * Set a flag that this is not a pre-check-in and was instead started at the kiosk or
         * from the "I'm Here" button. This will bypass the pre-check-in screen
         * letting the user complete the check-in process on their mobile device
         */
        appointmentDispatch({
            type: AppointmentDispatchTypes.AllowFullMobileCheckin,
            payload: true,
        });

        /**
         * Check if we ended up here due to the user clicking on the
         * "I'm Here" button in their email or pre-check-in screen.
         * If so, an apptkey/dmsApptKey param will be passed in
         */
        const parsed = qs.parse(search, { ignoreQueryPrefix: true });
        let apptKey: string = '';
        let isDmsApptKey: boolean = false;
        if ('dmsApptKey' in parsed && typeof parsed.dmsApptKey === 'string') {
            apptKey = parsed.dmsApptKey;
            isDmsApptKey = true;
            if ('dealerKey' in parsed && typeof parsed.dealerKey === 'string') {
                const newDealer = additionalDealers.find((i) => i.key === parsed.dealerKey);
                newDealer && newDealer.key !== kiosk.dealer.key && switchDealer(newDealer);
            }
        }
        else if ('apptkey' in parsed && typeof parsed.apptkey === 'string') {
            apptKey = parsed.apptkey;
        }
        if (apptKey) {
            const confirmParams: ConfirmInformationQueryParams = {
                isHere: 'true',
                ...(isDmsApptKey ? { isDmsApptKey: 'true' } : {}),
            };
            navigateAndRecord(
                `${PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Confirm)}${qs.stringify(confirmParams, { addQueryPrefix: true })}`,
                undefined,
                apptKey,
            );
        }
        else {
            /**
             * Set a flag that we landed here from the user scanning the QR code on
             * the kiosk wrap. We'll skip the advisor option screen since they
             * are already here
             */
            appointmentDispatch({
                type: AppointmentDispatchTypes.QRScannedAtLocker,
                payload: true,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Begins the drop off process for the outdoor kiosk
     */
    const onCheckIn = async () => {
        navigate(AppRoutePaths.Landing + AppRoutePaths.Appointment);
    };

    /**
     * Begins the pickup process for the outdoor kiosk
     */
    const onPickup = async () => {
        navigate(AppRoutePaths.OutdoorPickUp);
    };

    /**
     * Begins the tow-in process for the outdoor kiosk
     */
    const onTowIn = async () => {
        navigate(PathName(AppRoutePaths.Customer, CustomerRoutePaths.Add, Customer_AddRoutesPaths.TowCompanyDetails));
    };

    return (
        <div className={css.landingPage}>
            <div className={css.expressCheckIn}>
                <div className={css.checkInImage}>
                    <ThemeImage themeImageType={'landingPageBanner'} />
                </div>
                {kiosk.dealer.mobileCheckInEnabled && (
                    <>
                        <GMButton
                            id={Localization.WelcomeTapToStartCheckingIn}
                            title={Localization.WelcomeTapToStartCheckingIn}
                            ariaLabel={Localization.WelcomeTapToStartCheckingIn}
                            onClick={onCheckIn}
                            landingPageStyle
                        >
                            <div className={css.buttonContent}>
                                <div className={css.leftIcon}>
                                    <GMRoundedIcon
                                        themeType={'primary'}
                                        roundedBackgroundSize={'small'}
                                        landingPageStyle
                                    >
                                        <GMSizedIcon iconType={'Check-In'} iconSize={'large'} />
                                    </GMRoundedIcon>
                                </div>
                                <div className={css.buttonText}>
                                    <div>
                                        <div className={css.heading}>{Localization.CheckIn}</div>
                                        <div className={css.mainContent}>{Localization.WelcomeTapToStartCheckingIn}</div>
                                    </div>
                                </div>
                                <div className={css.arrowIcon}>
                                    <GMSizedIcon
                                        iconType={'Arrow'}
                                        iconSize={'medium'}
                                        ariaLabel={Localization.BackArrow}
                                    />
                                </div>
                            </div>
                        </GMButton>
                        {kiosk.keyPickupOption && (
                            <GMButton
                                id={Localization.YourRideAwaitsTapToCompleteYourVehiclePickUp}
                                title={Localization.YourRideAwaitsTapToCompleteYourVehiclePickUp}
                                ariaLabel={Localization.YourRideAwaitsTapToCompleteYourVehiclePickUp}
                                onClick={onPickup}
                                landingPageStyle
                            >
                                <div className={css.buttonContent}>
                                    <div className={css.leftIcon}>
                                        <GMRoundedIcon
                                            themeType={'primary'}
                                            roundedBackgroundSize={'small'}
                                            landingPageStyle
                                        >
                                            <GMSizedIcon iconType={'Vehicle'} iconSize={'large'} />
                                        </GMRoundedIcon>
                                    </div>
                                    <div className={css.buttonText}>
                                        <div>
                                            <div className={css.heading}>{Localization.PickUp}</div>
                                            <div className={css.mainContent}>{Localization.YourRideAwaitsTapToCompleteYourVehiclePickUp}</div>
                                        </div>
                                    </div>
                                    <div className={css.arrowIcon}>
                                        <GMSizedIcon
                                            iconType={'Arrow'}
                                            iconSize={'medium'}
                                            ariaLabel={Localization.BackArrow}
                                        />
                                    </div>
                                </div>
                            </GMButton>
                        )}
                        {towInSupport && (
                            <GMButton
                                id={Localization.TowCompaniesTapToGetStarted}
                                title={Localization.TowCompaniesTapToGetStarted}
                                ariaLabel={Localization.TowCompaniesTapToGetStarted}
                                onClick={onTowIn}
                                landingPageStyle
                            >
                                <div className={css.buttonContent}>
                                    <div className={css.leftIcon}>
                                        <GMRoundedIcon
                                            themeType={'primary'}
                                            roundedBackgroundSize={'small'}
                                            landingPageStyle
                                        >
                                            <GMSizedIcon iconType={'Tow-Truck'} iconSize={'large'} />
                                        </GMRoundedIcon>
                                    </div>
                                    <div className={css.buttonText}>
                                        <div>
                                            <div className={css.heading}>{Localization.TowIn}</div>
                                            <div className={css.mainContent}>{Localization.TowCompaniesTapToGetStarted}</div>
                                        </div>
                                    </div>
                                    <div className={css.arrowIcon}>
                                        <GMSizedIcon
                                            iconType={'Arrow'}
                                            iconSize={'medium'}
                                            ariaLabel={Localization.BackArrow}
                                        />
                                    </div>
                                </div>
                            </GMButton>
                        )}
                    </>
                )}
            </div>
            <div className={css.languages}>
                <LocalizationButtons displayAsLinks />
            </div>
            <div className={css.dealerInformation}>
                <div className={css.themeIconBanner}>
                    <ThemeImage themeImageType={'logo'} />
                </div>
                <h2 className={css.dealerName}>{kiosk.dealer.name}</h2>
                <h3 className={css.dealerAddress}>
                    <div>{kiosk.dealer.street}</div>
                    <div>
                        {kiosk.dealer.city}{', '}{kiosk.dealer.stateOrProvince} {kiosk.dealer.postalCode}
                    </div>
                </h3>
                <h4 className={css.dealerNumber}>
                    <a href={'tel:' + kiosk.dealer.phone}>{formatAsPhoneNumber(kiosk ? kiosk.dealer.phone : '')}</a>
                </h4>
            </div>
            <GMAccessibilityButton onLandingPage />
        </div>
    );
};

export { OutdoorLanding as default };